import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import { postCompressed } from '@/lib/post-compressed';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Upload, ArrowRight, ArrowLeft, Check, AlertCircle, ChevronDown, FileText, RefreshCw, Package, Zap, Pencil, Info, Loader2, Tag, Pipette, Download } from 'lucide-react';
import { format } from 'date-fns';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import {
  SORAFLO_IMPORT_CHUNK_TARGET_BYTES,
  type ShopifyCsvChunk,
  splitShopifyCsvByProduct,
} from '@/lib/shopify-csv-chunks';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

const SHOPIFY_FIELDS = [
  { key: 'title', label: 'Title', required: true },
  { key: 'body_html', label: 'Description (HTML)' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'product_type', label: 'Product Type' },
  { key: 'tags', label: 'Tags (comma-separated)' },
  { key: 'handle', label: 'URL Handle' },
  { key: 'variant_sku', label: 'Variant SKU' },
  { key: 'variant_price', label: 'Variant Price', required: true },
  { key: 'variant_cost', label: 'Cost Price (per unit)' },
  { key: 'variant_compare_at_price', label: 'Compare At Price' },
  { key: 'variant_barcode', label: 'Barcode' },
  { key: 'variant_weight', label: 'Weight value' },
  { key: 'variant_weight_unit', label: 'Weight unit' },
  { key: 'variant_inventory_qty', label: 'Inventory Quantity' },
  { key: 'option1_name', label: 'Option 1 Name (e.g. Size)' },
  { key: 'option1_value', label: 'Option 1 Value' },
  { key: 'option2_name', label: 'Option 2 Name (e.g. Color)' },
  { key: 'option2_value', label: 'Option 2 Value' },
  { key: 'option3_name', label: 'Option 3 Name' },
  { key: 'option3_value', label: 'Option 3 Value' },
  { key: 'image_src', label: 'Image URL 1' },
  { key: 'image_alt', label: 'Image 1 Alt Text' },
  { key: 'image_src_2', label: 'Image URL 2' },
  { key: 'image_alt_2', label: 'Image 2 Alt Text' },
  { key: 'image_src_3', label: 'Image URL 3' },
  { key: 'image_alt_3', label: 'Image 3 Alt Text' },
  { key: 'image_src_4', label: 'Image URL 4' },
  { key: 'image_alt_4', label: 'Image 4 Alt Text' },
  { key: 'image_src_5', label: 'Image URL 5' },
  { key: 'image_alt_5', label: 'Image 5 Alt Text' },
];

interface MetafieldDef {
  namespace: string;
  key: string;
  name: string;
  type: string;
  ownerType: string;
}

interface MetafieldColMap {
  namespace: string;
  key: string;
  name: string;
  type: string;
  csvColumn: string;
}

function autoMap(headers: string[]): Record<string, string> {
  const lower = headers.map(h => ({ original: h, lower: h.toLowerCase().replace(/[^a-z0-9]/g, '') }));
  const mapping: Record<string, string> = {};

  // Extra aliases for non-obvious column names (Shopify exports, third-party formats, etc.)
  const extraAliases: Record<string, string[]> = {
    title:                    ['name', 'productname', 'producttitle'],
    body_html:                ['bodyhtml', 'description', 'body', 'content', 'details'],
    vendor:                   ['brand', 'manufacturer', 'supplier'],
    product_type:             ['type', 'category', 'productcategory'],
    tags:                     ['tag', 'keywords'],
    handle:                   ['slug', 'urlhandle', 'url'],
    variant_sku:              ['sku', 'itemcode', 'code', 'partnumber'],
    variant_price:            ['price', 'saleprice', 'unitprice', 'sellprice'],
    variant_cost:             ['cost', 'costprice', 'unitcost', 'purchaseprice', 'buyprice', 'landedcost', 'tradeprice', 'costperitem'],
    variant_compare_at_price: ['compareatprice', 'compareatsaleprice', 'rrp', 'msrp', 'originalprice', 'wasprice'],
    variant_barcode:          ['barcode', 'upc', 'ean', 'gtin'],
    variant_weight:           ['weight', 'weightkg', 'weightgrams', 'weightvaluegrams', 'variantgrams'],
    variant_weight_unit:      ['weightunit', 'weightunitfordisplay', 'unit'],
    variant_inventory_qty:    ['quantity', 'qty', 'stock', 'inventory', 'inventoryqty', 'inventoryquantity'],
    option1_name:             ['optionname', 'sizename', 'colorname'],
    option1_value:            ['option1', 'size', 'colour', 'color'],
    option2_name:             [],
    option2_value:            ['option2'],
    option3_name:             [],
    option3_value:            ['option3'],
    image_src:                ['imageurl', 'image', 'photo', 'thumbnail', 'img', 'productimageurl', 'variantimageurl', 'variantimage', 'imagesrc1', 'imageurl1', 'image1'],
    image_alt:                ['imagealt', 'alttext', 'alt', 'imagealttext', 'imagealt1', 'alt1'],
    image_src_2:              ['imagesrc2', 'imageurl2', 'image2', 'photo2', 'img2'],
    image_alt_2:              ['imagealt2', 'alt2', 'imagealttext2'],
    image_src_3:              ['imagesrc3', 'imageurl3', 'image3', 'photo3', 'img3'],
    image_alt_3:              ['imagealt3', 'alt3', 'imagealttext3'],
    image_src_4:              ['imagesrc4', 'imageurl4', 'image4', 'photo4', 'img4'],
    image_alt_4:              ['imagealt4', 'alt4', 'imagealttext4'],
    image_src_5:              ['imagesrc5', 'imageurl5', 'image5', 'photo5', 'img5'],
    image_alt_5:              ['imagealt5', 'alt5', 'imagealttext5'],
  };

  for (const field of SHOPIFY_FIELDS) {
    // The normalized field key (strip underscores) matches Shopify's own CSV headers exactly,
    // e.g. variant_compare_at_price → variantcompareatprice = "Variant Compare At Price" normalised.
    const keyNorm = field.key.replace(/_/g, '');
    const candidates = [keyNorm, ...(extraAliases[field.key] ?? [])];
    for (const col of lower) {
      if (candidates.includes(col.lower)) {
        mapping[field.key] = col.original;
        break;
      }
    }
  }
  return mapping;
}

/** Try to match each metafield definition against the CSV headers using normalized string comparison. */
function autoMapMetafields(defs: MetafieldDef[], csvHeaders: string[]): Record<string, string> {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const normalizedHeaders = csvHeaders.map(h => ({ original: h, lower: norm(h) }));
  const result: Record<string, string> = {};
  for (const def of defs) {
    const mapKey = `${def.namespace}.${def.key}`;
    // Candidates: key alone, name, namespace.key (both joined and dotted), namespace+key
    const candidates = [
      norm(def.key),
      norm(def.name),
      norm(`${def.namespace}.${def.key}`),
      norm(`${def.namespace}${def.key}`),
    ];
    for (const col of normalizedHeaders) {
      if (candidates.includes(col.lower)) {
        result[mapKey] = col.original;
        break;
      }
    }
  }
  return result;
}

function cleanShopifyError(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed)
        .map(([field, msgs]) => {
          const list = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
          return `${field}: ${list}`;
        })
        .join(' | ');
    }
  } catch {
    // not JSON, return as-is
  }
  return raw;
}

function statusBadge(status: string) {
  if (status === 'done') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Done</span>;
  if (status === 'running') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">Running</span>;
  if (status === 'failed') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">Failed</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-bold bg-slate-500/10 text-muted-foreground border border-slate-500/20">Pending</span>;
}

export function Import() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [storeId, setStoreId] = useState('');
  const [filename, setFilename] = useState('');
  const [csvText, setCsvText] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<Record<string, string>[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'overwrite' | 'create'>('skip');
  const [productStatus, setProductStatus] = useState<'active' | 'draft' | 'archived'>('active');
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [result, setResult] = useState<{ successCount: number; skipCount: number; errorCount: number; errors: { title: string; error: string }[] } | null>(null);
  const [autoDetected, setAutoDetected] = useState(false);
  const [importMode, setImportMode] = useState<'import' | 'update' | 'patch' | 'price'>('import');
  const [previousMapping, setPreviousMapping] = useState<Record<string, string> | null>(null);
  const [pendingAutoAdvance, setPendingAutoAdvance] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [retryProgress, setRetryProgress] = useState<{ current: number; total: number } | null>(null);
  const [retryResultMap, setRetryResultMap] = useState<Record<string, { successCount: number; skipCount: number; errorCount: number; errors: { title: string; error: string }[] }>>({});
  const [dismissingJobId, setDismissingJobId] = useState<string | null>(null);
  const [templateSuffix, setTemplateSuffix] = useState('');
  const [continueSellingOos, setContinueSellingOos] = useState(false);
  const [trackQty, setTrackQty] = useState(true);
  const [metaColMaps, setMetaColMaps] = useState<MetafieldColMap[]>([]);
  const [titlePrefixCol, setTitlePrefixCol] = useState('');
  const [titleSuffixCol, setTitleSuffixCol] = useState('');
  const [titleSep, setTitleSep] = useState(' ');
  const [shopifyCsvChunks, setShopifyCsvChunks] = useState<ShopifyCsvChunk[]>([]);

  const { data: stores = [] } = useQuery<any[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load stores');
      return res.json();
    },
    enabled: !!token,
  });

  const { data: jobs = [], refetch: refetchJobs } = useQuery<any[]>({
    queryKey: ['import-jobs'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/imports`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load import history');
      return res.json();
    },
    enabled: !!token,
    // Poll every 2 s while any job is still running so the history bar stays live
    refetchInterval: (data) => {
      const hasRunning = (Array.isArray(data) ? data : (data as any)?.state?.data ?? [])
        .some((j: any) => j.status === 'running');
      return hasRunning ? 2000 : false;
    },
  });

  // Fetch product templates from the selected store's active theme
  const { data: productTemplates = [] } = useQuery<{ label: string; value: string }[]>({
    queryKey: ['product-templates', storeId],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/imports/templates?storeId=${storeId}`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load product templates');
      return res.json();
    },
    enabled: !!storeId && !!token,
    staleTime: 60_000,
  });

  // Fetch metafield definitions for the selected store (product owner type only)
  const { data: metafieldDefs = [], isLoading: metaDefsLoading } = useQuery<MetafieldDef[]>({
    queryKey: ['metafield-defs', storeId],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores/${storeId}/metafields/definitions`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load metafield definitions');
      const data = await res.json();
      // API returns a plain array; the GraphQL query already scopes to PRODUCT owner type
      return Array.isArray(data) ? data : (data.definitions ?? []);
    },
    enabled: !!storeId && !!token,
    staleTime: 120_000,
  });

  // Initialise metaColMaps whenever definitions load or headers change.
  // Priority: stored mapping (retry flow) > auto-mapped from headers > empty.
  useEffect(() => {
    if (!metafieldDefs.length) return;
    const storedMeta: MetafieldColMap[] = (previousMapping as any)?.__metafields__ ?? [];
    const autoMapped = headers.length ? autoMapMetafields(metafieldDefs, headers) : {};
    setMetaColMaps(metafieldDefs.map(def => {
      const mapKey = `${def.namespace}.${def.key}`;
      const stored = storedMeta.find(s => s.namespace === def.namespace && s.key === def.key);
      return {
        namespace: def.namespace,
        key: def.key,
        name: def.name,
        type: def.type,
        csvColumn: stored?.csvColumn ?? autoMapped[mapKey] ?? '',
      };
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metafieldDefs, headers]);

  // Auto-detect store from ?shop= URL param or single connected store
  useEffect(() => {
    if (!stores.length || storeId) return;
    const params = new URLSearchParams(window.location.search);
    const shopParam = params.get('shop')?.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (shopParam) {
      const match = stores.find((s: any) =>
        s.storeUrl.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '') === shopParam
      );
      if (match) { setStoreId(match.id); setAutoDetected(true); return; }
    }
    if (stores.length === 1) { setStoreId(stores[0].id); setAutoDetected(true); }
  }, [stores, storeId]);

  const startUpdate = (job: any) => {
    reset();
    setStoreId(job.storeId);
    setDuplicateHandling('overwrite');
    setImportMode('update');
    const raw = job.columnMapping ?? null;
    setPreviousMapping(raw);
    if (raw?.__titlePrefixCol__) setTitlePrefixCol(raw.__titlePrefixCol__);
    if (raw?.__titleSuffixCol__) setTitleSuffixCol(raw.__titleSuffixCol__);
    if (raw?.__titleSep__ !== undefined) setTitleSep(raw.__titleSep__);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const retryImport = async (jobId: string) => {
    setRetryingJobId(jobId);
    setRetryProgress(null);
    setRetryResultMap(prev => { const n = { ...prev }; delete n[jobId]; return n; });
    try {
      const res = await fetch(`${BASE}/api/imports/${jobId}/retry`, {
        method: 'POST',
        headers: authOpts.headers,
      });
      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Retry failed');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let receivedDone = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          try {
            const event = JSON.parse(line.slice(5).trim());
            if (event.type === 'progress') setRetryProgress({ current: event.current, total: event.total });
            if (event.type === 'done') {
              receivedDone = true;
              setRetryResultMap(prev => ({ ...prev, [jobId]: { successCount: event.successCount, skipCount: event.skipCount, errorCount: event.errorCount, errors: event.errors } }));
              refetchJobs();
            }
          } catch {}
        }
      }
      if (!receivedDone) throw new Error('Retry stream ended before completion. Check import history before retrying again.');
    } catch (err: any) {
      toast({ title: 'Retry failed', description: err.message, variant: 'destructive' });
    } finally {
      setRetryingJobId(null);
      setRetryProgress(null);
    }
  };

  const dismissJob = async (jobId: string) => {
    setDismissingJobId(jobId);
    try {
      const res = await fetch(`${BASE}/api/imports/${jobId}/dismiss`, {
        method: 'PATCH',
        headers: authOpts.headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Dismiss failed');
      }
      refetchJobs();
    } catch (err: any) {
      toast({ title: 'Could not dismiss job', description: err.message, variant: 'destructive' });
    } finally {
      setDismissingJobId(null);
    }
  };

  const processFile = useCallback(async (file: File) => {
    setFilename(file.name);
    const text = await file.text();
    setCsvText(text);
    setShopifyCsvChunks([]);
    let chunks: ShopifyCsvChunk[] = [];
    if (file.size > SORAFLO_IMPORT_CHUNK_TARGET_BYTES) {
      try {
        chunks = splitShopifyCsvByProduct(text, file.name, SORAFLO_IMPORT_CHUNK_TARGET_BYTES);
        setShopifyCsvChunks(chunks);
        toast({
          title: `Large CSV split into ${chunks.length} parts`,
          description: 'SoraFlo will import every part automatically and keep complete products together.',
        });
      } catch (err: any) {
        toast({
          title: 'Could not split CSV safely',
          description: err.message,
          variant: 'destructive',
        });
      }
    }
    setIsParsing(true);
    try {
      const parseText = chunks[0]?.text ?? text;
      const res = await postCompressed(`${BASE}/api/imports/parse`, { csvText: parseText, filename: file.name }, { headers: authOpts.headers });
      if (res.status === 413) throw new Error('CSV file is too large. Please split it into smaller files and try again.');
      const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
      if (!res.ok) throw new Error(data.error ?? 'Parse failed');
      setHeaders(data.headers);
      setSampleRows(data.sampleRows);
      setTotalRows(chunks.length > 1 ? chunks.reduce((sum, chunk) => sum + chunk.rowCount, 0) : data.totalRows);

      // Build merged mapping: previous job's mapping (for columns present in new CSV)
      // takes priority; auto-map fills in any fields that weren't previously mapped.
      const autoMapped = autoMap(data.headers);
      if (previousMapping) {
        const merged: Record<string, string> = {};
        // Step 1: restore previous mappings that exist in the new CSV headers
        for (const [field, col] of Object.entries(previousMapping)) {
          if (col && data.headers.includes(col)) merged[field] = col;
        }
        // Step 2: auto-map any fields not already covered
        for (const [field, col] of Object.entries(autoMapped)) {
          if (!merged[field]) merged[field] = col;
        }
        setMapping(merged);
      } else {
        setMapping(autoMapped);
      }
    } catch (err: any) {
      toast({ title: 'Parse Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsParsing(false);
    }
  }, [authOpts.headers, toast, previousMapping]);

  const downloadShopifyChunk = (chunk: ShopifyCsvChunk) => {
    const url = URL.createObjectURL(new Blob([chunk.text], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = chunk.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  // Pre-load a CSV that was passed from the BC Scraper page via sessionStorage
  useEffect(() => {
    const scraped = sessionStorage.getItem('soraflo-scraped-csv');
    const scrapedFilename = sessionStorage.getItem('soraflo-scraped-filename');
    if (scraped && scrapedFilename) {
      sessionStorage.removeItem('soraflo-scraped-csv');
      sessionStorage.removeItem('soraflo-scraped-filename');
      const blob = new Blob([scraped], { type: 'text/csv' });
      const file = new File([blob], scrapedFilename, { type: 'text/csv' });
      setPendingAutoAdvance(true);
      processFile(file);
    }
  }, [processFile]);

  // Auto-advance to step 2 (column mapping) once the scraped CSV is parsed AND a store is selected.
  // If no store has been selected yet (multiple stores, no auto-detect), stay on step 1 so the user
  // can pick their destination store before proceeding.
  useEffect(() => {
    if (pendingAutoAdvance && headers.length > 0 && !isParsing && storeId) {
      setPendingAutoAdvance(false);
      setStep(2);
    }
  }, [pendingAutoAdvance, headers.length, isParsing, storeId]);

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      toast({ title: 'Invalid file', description: 'Please upload a CSV file.', variant: 'destructive' });
      return;
    }
    processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const canProceedStep1 = storeId && headers.length > 0;
  const canProceedStep2 = importMode === 'patch'
    ? (!!mapping['handle'] || !!overrides['handle'] || !!mapping['title'] || !!overrides['title']) &&
      Object.entries({ ...mapping, ...overrides }).some(([k]) => k !== 'handle' && k !== 'title')
    : importMode === 'price'
    ? (!!mapping['variant_sku'] || !!overrides['variant_sku']) && (!!mapping['variant_price'] || !!overrides['variant_price'])
    : SHOPIFY_FIELDS.filter(f => f.required).every(f => mapping[f.key] || overrides[f.key]);

  // Count unique products by grouping CSV rows by handle (or title) using the current mapping.
  // This gives an accurate "Products to import" count rather than raw CSV row count.
  const productCount = useMemo(() => {
    if (!csvText) return null;
    const handleCol = mapping['handle'] ?? '';
    const titleCol = mapping['title'] ?? '';
    const handleOverride = overrides['handle'] ?? '';
    const titleOverride = overrides['title'] ?? '';
    // If both are fixed overrides (same value every row) → always 1 product
    if (handleOverride && titleOverride) return 1;
    if (!handleCol && !titleCol && !handleOverride && !titleOverride) return null;
    try {
      const parsed = Papa.parse<Record<string, string>>(csvText.trim(), {
        header: true, skipEmptyLines: true, dynamicTyping: false,
      });
      const seen = new Set<string>();
      for (const row of parsed.data as Record<string, string>[]) {
        const key = handleOverride || (handleCol ? row[handleCol]?.trim() ?? '' : '')
          || titleOverride || (titleCol ? row[titleCol]?.trim() ?? '' : '');
        if (key) seen.add(key);
      }
      return seen.size || null;
    } catch {
      return null;
    }
  }, [csvText, mapping, overrides]);

  const runImport = async () => {
    setIsImporting(true);
    setImportProgress(null);
    setResult(null);
    try {
      const importParts = shopifyCsvChunks.length > 1
        ? shopifyCsvChunks
        : [{ filename, text: csvText, productCount: productCount ?? totalRows }];
      const overallTotal = importParts.reduce((sum, part) => sum + part.productCount, 0);
      let completedBeforePart = 0;
      const combinedResult = {
        successCount: 0,
        skipCount: 0,
        errorCount: 0,
        errors: [] as { title: string; error: string }[],
      };

      for (const part of importParts) {
        const res = await postCompressed(`${BASE}/api/imports/run`, {
          storeId,
          filename: part.filename,
          csvText: part.text,
          mapping,
          overrides,
          metafieldMappings: metaColMaps.filter(m => m.csvColumn),
          duplicateHandling: importMode === 'patch' ? 'patch' : duplicateHandling,
          productStatus,
          templateSuffix,
          continueSellingOos,
          trackQty,
          titlePrefixCol: titlePrefixCol || undefined,
          titleSuffixCol: titleSuffixCol || undefined,
          titleSep: titleSep || ' ',
          priceOnly: importMode === 'price',
        }, { headers: authOpts.headers });
        if (!res.ok || !res.body) {
          const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
          throw new Error(data.error ?? `Import failed for ${part.filename}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedDone = false;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'progress') {
                setImportProgress({
                  current: completedBeforePart + event.current,
                  total: overallTotal,
                });
              } else if (event.type === 'done') {
                receivedDone = true;
                combinedResult.successCount += event.successCount;
                combinedResult.skipCount += event.skipCount;
                combinedResult.errorCount += event.errorCount;
                combinedResult.errors.push(...(event.errors ?? []));
              }
            } catch { /* ignore malformed lines */ }
          }
        }
        if (!receivedDone) {
          throw new Error(`Import stream ended before ${part.filename} completed. Check import history before trying again.`);
        }
        completedBeforePart += part.productCount;
      }

      setResult(combinedResult);
      setImportProgress({ current: overallTotal, total: overallTotal });
      refetchJobs();
    } catch (err: any) {
      toast({ title: 'Import Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  };

  const reset = () => {
    setStep(1);
    setStoreId('');
    setFilename('');
    setCsvText('');
    setHeaders([]);
    setSampleRows([]);
    setTotalRows(0);
    setMapping({});
    setOverrides({});
    setResult(null);
    setImportProgress(null);
    setAutoDetected(false);
    setProductStatus('active');
    setContinueSellingOos(false);
    setTrackQty(true);
    setTemplateSuffix('');
    setImportMode('import');
    setPreviousMapping(null);
    setMetaColMaps([]);
    setTitlePrefixCol('');
    setTitleSuffixCol('');
    setTitleSep(' ');
    if (fileRef.current) fileRef.current.value = '';
  };

  const selectedStore = stores.find((s: any) => s.id === storeId);

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-gradient">
              {importMode === 'update' ? 'Update Products' : importMode === 'patch' ? 'Field Patch' : importMode === 'price' ? 'Price Update' : 'Product Importer'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {importMode === 'update'
                ? 'Upload a CSV to update existing products in Shopify — matched by handle'
                : importMode === 'patch'
                ? 'Update only the fields you choose — existing data you don\'t map is left untouched'
                : importMode === 'price'
                ? 'Update variant prices only — matched by SKU, nothing else is touched'
                : 'Upload any CSV and map columns to import products into Shopify'}
            </p>
          </div>
          {(importMode === 'update' || importMode === 'patch' || importMode === 'price') && (
            <button
              onClick={reset}
              className="text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 transition-all"
            >
              Switch to Import
            </button>
          )}
        </div>

        {/* Update mode banner */}
        {importMode === 'update' && (
          <div className="flex items-center gap-3 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
            <Pencil className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>Update mode</strong> — products that already exist in Shopify will be overwritten. Products not found will be created.
            </span>
          </div>
        )}
        {importMode === 'patch' && (
          <div className="flex items-center gap-3 text-sm text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded-xl px-4 py-3">
            <Pipette className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>Field Patch mode</strong> — only the fields you map will be updated. Everything else on each product stays exactly as it is. Products not found in Shopify are skipped.
            </span>
          </div>
        )}
        {importMode === 'price' && (
          <div className="flex items-center gap-3 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3">
            <Tag className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>Price Update mode</strong> — only variant prices are updated, matched by SKU. No products are created or modified in any other way. Much faster than a full import.
            </span>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {[
            { n: 1, label: 'Upload CSV' },
            { n: 2, label: 'Map Columns' },
            { n: 3, label: importMode === 'update' ? 'Review & Update' : importMode === 'patch' ? 'Review & Patch' : importMode === 'price' ? 'Review & Update Prices' : 'Review & Import' },
          ].map(({ n, label }, i) => (
            <React.Fragment key={n}>
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border transition-all",
                  step === n ? "bg-primary text-black border-primary shadow-[0_0_10px_rgba(0,240,255,0.4)]" :
                  step > n ? "bg-primary/20 text-primary border-primary/40" :
                  "bg-muted/50 text-muted-foreground border-border"
                )}>
                  {step > n ? <Check className="w-4 h-4" /> : n}
                </div>
                <span className={cn("text-sm font-medium hidden sm:block", step === n ? "text-foreground" : "text-muted-foreground")}>{label}</span>
              </div>
              {i < 2 && <div className={cn("flex-1 h-px", step > n ? "bg-primary/40" : "bg-muted")} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Store + Upload */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Mode selector — only show when not triggered from update/retry flow */}
            {importMode !== 'update' && (
              <div className="glass-panel rounded-2xl p-6 space-y-3">
                <h2 className="font-semibold text-foreground">What would you like to do?</h2>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setImportMode('import')}
                    className={cn(
                      "p-4 rounded-xl border text-left transition-all",
                      importMode === 'import' ? "border-primary bg-primary/10" : "border-border bg-muted/30 hover:border-primary/40"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Upload className={cn("w-4 h-4", importMode === 'import' ? "text-primary" : "text-muted-foreground")} />
                      <p className={cn("text-sm font-semibold", importMode === 'import' ? "text-primary" : "text-foreground")}>Import / Create</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Upload a CSV to create new products or overwrite existing ones</p>
                  </button>
                  <button
                    onClick={() => setImportMode('patch')}
                    className={cn(
                      "p-4 rounded-xl border text-left transition-all",
                      importMode === 'patch' ? "border-sky-500 bg-sky-500/10" : "border-border bg-muted/30 hover:border-sky-500/40"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Pipette className={cn("w-4 h-4", importMode === 'patch' ? "text-sky-400" : "text-muted-foreground")} />
                      <p className={cn("text-sm font-semibold", importMode === 'patch' ? "text-sky-400" : "text-foreground")}>Field Patch</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Update only specific fields on existing products — nothing else is touched</p>
                  </button>
                  <button
                    onClick={() => setImportMode('price')}
                    className={cn(
                      "p-4 rounded-xl border text-left transition-all",
                      importMode === 'price' ? "border-emerald-500 bg-emerald-500/10" : "border-border bg-muted/30 hover:border-emerald-500/40"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Tag className={cn("w-4 h-4", importMode === 'price' ? "text-emerald-400" : "text-muted-foreground")} />
                      <p className={cn("text-sm font-semibold", importMode === 'price' ? "text-emerald-400" : "text-foreground")}>Price Update</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Update variant prices only, matched by SKU — fast, nothing else is changed</p>
                  </button>
                </div>
              </div>
            )}

            {/* Store selector */}
            <div className="glass-panel rounded-2xl p-6 space-y-4">
              {autoDetected && storeId && (
                <div className="flex items-center gap-2 text-sm text-primary bg-primary/10 border border-primary/20 rounded-xl px-4 py-3">
                  <Zap className="w-4 h-4 flex-shrink-0" />
                  Store auto-detected from your Shopify installation. You can change it below if needed.
                </div>
              )}
              {stores.length === 0 ? (
                <p className="text-sm text-muted-foreground">No stores connected. Add a store first.</p>
              ) : (
                <ShopifyTargetStoreSelector
                  stores={stores}
                  value={storeId}
                  onChange={id => { setStoreId(id); setAutoDetected(false); }}
                  label="Destination Store"
                  variant="tiles"
                />
              )}
            </div>

            {/* File upload */}
            <div className="glass-panel rounded-2xl p-6 space-y-4">
              <h2 className="font-semibold text-foreground">Upload CSV File</h2>
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={cn(
                  "relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all",
                  isDragging ? "border-primary bg-primary/10" : "border-border hover:border-border hover:bg-muted/50"
                )}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
                {isParsing ? (
                  <div className="flex flex-col items-center gap-3">
                    <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                    <p className="text-sm text-muted-foreground">Parsing CSV…</p>
                  </div>
                ) : headers.length > 0 ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{filename}</p>
                      <p className="text-sm text-muted-foreground mt-1">{totalRows.toLocaleString()} rows · {headers.length} columns detected</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Click to replace</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Drop your CSV here</p>
                      <p className="text-sm text-muted-foreground mt-1">or click to browse · Any format accepted</p>
                    </div>
                  </div>
                )}
              </div>

              {shopifyCsvChunks.length > 1 && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Large CSV ready for automatic import
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      SoraFlo split it into {shopifyCsvChunks.length} product-safe parts and will import them in order automatically. Configure the import once and continue normally.
                    </p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {shopifyCsvChunks.map((chunk) => (
                      <Button
                        key={chunk.filename}
                        variant="outline"
                        className="h-auto justify-start gap-3 py-3"
                        onClick={(event) => {
                          event.stopPropagation();
                          downloadShopifyChunk(chunk);
                        }}
                      >
                        <Download className="w-4 h-4 shrink-0" />
                        <span className="text-left">
                          <span className="block text-sm">{chunk.filename}</span>
                          <span className="block text-xs font-normal text-muted-foreground">
                            {(chunk.bytes / 1_000_000).toFixed(1)} MB · {chunk.productCount.toLocaleString()} products
                          </span>
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sample preview */}
              {sampleRows.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        {headers.map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sampleRows.map((row, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-muted/50">
                          {headers.map(h => (
                            <td key={h} className="px-3 py-2 text-foreground whitespace-nowrap max-w-[200px] truncate">{row[h] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canProceedStep1}>
                Map Columns <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-foreground">Map Your Columns</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {importMode === 'update' && previousMapping
                      ? 'Previously mapped fields are pre-filled. Map any new fields you want to update.'
                      : 'Match your CSV columns to Shopify product fields. Required fields are marked.'}
                  </p>
                </div>
                <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-md border border-primary/20">
                  {headers.length} columns in file
                </span>
              </div>
              {importMode === 'update' && previousMapping && (
                <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  <Pencil className="w-3 h-3 flex-shrink-0" />
                  Previous mappings restored — only map additional fields you want to change.
                </div>
              )}

              {/* Column headers */}
              <div className="flex items-center gap-4 px-3 pb-1">
                <div className="w-48 flex-shrink-0" />
                <div className="w-4 flex-shrink-0" />
                <p className="flex-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">CSV Column</p>
                <p className="w-44 flex-shrink-0 text-xs font-medium text-muted-foreground uppercase tracking-wide">Fixed Override</p>
                <p className="w-40 flex-shrink-0 text-xs text-slate-600">Preview</p>
              </div>

              <div className="space-y-2">
                {SHOPIFY_FIELDS.map(field => {
                  const hasOverride = !!overrides[field.key];
                  const sampleVal = overrides[field.key]
                    ? overrides[field.key]
                    : mapping[field.key] && sampleRows[0]
                      ? (sampleRows[0][mapping[field.key]] ?? '–')
                      : null;

                  // For the Title field: build a combined preview including prefix/suffix
                  const titlePreview = field.key === 'title' && sampleRows[0] ? (() => {
                    const core = overrides['title'] || (mapping['title'] ? (sampleRows[0][mapping['title']] ?? '') : '');
                    const pre  = titlePrefixCol ? (sampleRows[0][titlePrefixCol] ?? '') : '';
                    const suf  = titleSuffixCol ? (sampleRows[0][titleSuffixCol] ?? '') : '';
                    return [pre, core, suf].filter(Boolean).join(titleSep || ' ');
                  })() : null;

                  return (
                    <React.Fragment key={field.key}>
                      <div className="flex items-center gap-4 p-3 rounded-xl bg-muted/50 border border-border/50 hover:border-border transition-all">
                        <div className="w-48 flex-shrink-0">
                          <p className="text-sm font-medium text-foreground">
                            {field.label}
                            {field.required && <span className="text-red-400 ml-1">*</span>}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                        {/* CSV column dropdown */}
                        <div className="relative flex-1">
                          <select
                            value={mapping[field.key] ?? ''}
                            onChange={e => setMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                            disabled={hasOverride}
                            className={cn(
                              "w-full h-9 appearance-none rounded-xl border border-border bg-muted/50 pl-3 pr-8 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all",
                              hasOverride && "opacity-30 cursor-not-allowed"
                            )}
                          >
                            <option value="">— Not mapped —</option>
                            {headers.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        {/* Fixed override text input */}
                        <input
                          type="text"
                          placeholder="type fixed value…"
                          value={overrides[field.key] ?? ''}
                          onChange={e => setOverrides(prev => {
                            const val = e.target.value;
                            const next = { ...prev };
                            if (val) next[field.key] = val; else delete next[field.key];
                            return next;
                          })}
                          className={cn(
                            "w-44 flex-shrink-0 h-9 rounded-xl border px-3 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:ring-1 transition-all bg-muted/50",
                            hasOverride
                              ? "border-primary/50 ring-1 ring-primary/30 bg-primary/5"
                              : "border-border focus:ring-primary/50"
                          )}
                        />
                        {/* Sample / preview */}
                        <div className="w-40 flex-shrink-0 text-xs truncate" title={titlePreview ?? sampleVal ?? ''}>
                          {(titlePreview || sampleVal)
                            ? <span className={hasOverride ? "text-primary/70" : "text-muted-foreground"}>
                                {hasOverride ? '✎ ' : 'e.g. '}{titlePreview ?? sampleVal}
                              </span>
                            : null}
                        </div>
                      </div>

                      {/* Title prefix / suffix controls — only shown on the Title row */}
                      {field.key === 'title' && (mapping['title'] || overrides['title']) && (
                        <div className="ml-6 pl-4 border-l-2 border-primary/20 py-2 space-y-2">
                          <p className="text-xs text-muted-foreground font-medium">Title prefix / suffix <span className="text-slate-600 font-normal">(optional — combine another column with the title)</span></p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Prefix column */}
                            <div className="relative">
                              <select
                                value={titlePrefixCol}
                                onChange={e => setTitlePrefixCol(e.target.value)}
                                className="h-8 appearance-none rounded-lg border border-border bg-muted/50 pl-2.5 pr-7 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                              >
                                <option value="">— No prefix —</option>
                                {headers.map(h => <option key={h} value={h}>{h}</option>)}
                              </select>
                              <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                            </div>
                            <span className="text-xs text-slate-500">+</span>
                            {/* Separator */}
                            <input
                              type="text"
                              value={titleSep}
                              onChange={e => setTitleSep(e.target.value)}
                              placeholder=" "
                              className="w-14 h-8 rounded-lg border border-border bg-muted/50 px-2 text-xs text-foreground text-center focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                              title="Separator between parts"
                            />
                            <span className="text-xs text-slate-500">Title</span>
                            <span className="text-xs text-slate-500">+</span>
                            {/* Separator (same value, shown for symmetry) */}
                            <span className="w-14 h-8 flex items-center justify-center text-xs text-slate-600 border border-border/30 rounded-lg bg-muted/20">{JSON.stringify(titleSep)}</span>
                            <span className="text-xs text-slate-500">+</span>
                            {/* Suffix column */}
                            <div className="relative">
                              <select
                                value={titleSuffixCol}
                                onChange={e => setTitleSuffixCol(e.target.value)}
                                className="h-8 appearance-none rounded-lg border border-border bg-muted/50 pl-2.5 pr-7 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                              >
                                <option value="">— No suffix —</option>
                                {headers.map(h => <option key={h} value={h}>{h}</option>)}
                              </select>
                              <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                            </div>
                            {/* Live preview */}
                            {titlePreview && (
                              <span className="text-xs text-primary/80 bg-primary/10 border border-primary/20 rounded-lg px-2 py-1 ml-1 truncate max-w-[260px]" title={titlePreview}>
                                → {titlePreview}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Metafield Mappings — shown when the store has product metafield definitions */}
              {(metaDefsLoading || metafieldDefs.length > 0) && (
                <div className="space-y-3 pt-4 border-t border-border/50">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-primary/70" />
                    <p className="text-sm font-medium text-foreground">
                      Metafield Mappings
                      <span className="text-xs text-muted-foreground font-normal ml-2">optional</span>
                    </p>
                  </div>
                  {metaDefsLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading metafield definitions…
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {metaColMaps.map((mf, i) => (
                        <div key={`${mf.namespace}.${mf.key}`} className="flex items-center gap-4 p-3 rounded-xl bg-muted/50 border border-border/50 hover:border-border transition-all">
                          <div className="w-48 flex-shrink-0">
                            <p className="text-sm font-medium text-foreground">{mf.name}</p>
                            <p className="text-xs text-slate-600">{mf.namespace}.{mf.key}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                          <div className="relative flex-1">
                            <select
                              value={mf.csvColumn}
                              onChange={e => setMetaColMaps(prev => prev.map((m, j) => j === i ? { ...m, csvColumn: e.target.value } : m))}
                              className="w-full h-9 appearance-none rounded-xl border border-border bg-muted/50 pl-3 pr-8 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
                            >
                              <option value="">— Not mapped —</option>
                              {headers.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                          <span className="w-44 flex-shrink-0 text-xs text-slate-600 italic truncate">{mf.type}</span>
                          <div className="w-36 flex-shrink-0 text-xs text-muted-foreground truncate">
                            {mf.csvColumn && sampleRows[0] ? `e.g. ${sampleRows[0][mf.csvColumn] ?? '–'}` : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!canProceedStep2 && (
                <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {importMode === 'patch'
                    ? 'Map a product identifier (Title or Handle) and at least one field to update to continue.'
                    : importMode === 'price'
                    ? 'Map Variant SKU and Variant Price to continue.'
                    : 'Map the required fields (Title and Variant Price) to continue.'}
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!canProceedStep2}>
                Review & Import <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review + Import */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6 space-y-6">
              <h2 className="font-semibold text-foreground">Review Import</h2>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Destination Store', value: selectedStore?.nickname ?? storeId },
                  { label: 'File', value: filename },
                  { label: 'CSV Rows', value: totalRows.toLocaleString(), sub: 'total lines in file' },
                  { label: 'Products to Import', value: productCount != null ? productCount.toLocaleString() : '—', sub: productCount != null ? 'grouped by handle' : 'map Title or Handle to count', highlight: productCount != null },
                ].map(({ label, value, sub, highlight }) => (
                  <div key={label} className={cn("rounded-xl border p-4", highlight ? "bg-primary/5 border-primary/20" : "bg-muted/50 border-border")}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className={cn("text-sm font-semibold mt-1 truncate", highlight ? "text-primary" : "text-foreground")}>{value}</p>
                    {sub && <p className="text-xs text-slate-600 mt-0.5 truncate">{sub}</p>}
                  </div>
                ))}
              </div>

              {/* Mapped fields preview */}
              <div>
                <p className="text-sm font-medium text-foreground mb-3">Active Mappings</p>
                <div className="grid grid-cols-2 gap-2">
                  {SHOPIFY_FIELDS.filter(f => overrides[f.key] || mapping[f.key]).map(field => (
                    <div key={field.key} className={cn(
                      "flex items-center gap-2 text-xs rounded-lg px-3 py-2",
                      overrides[field.key] ? "bg-primary/5 border border-primary/20 text-foreground" : "bg-muted/50 text-muted-foreground"
                    )}>
                      <span className="text-primary font-medium">{field.label}</span>
                      <ArrowLeft className="w-3 h-3 rotate-180" />
                      {overrides[field.key]
                        ? <><Pencil className="w-3 h-3 text-primary/60 flex-shrink-0" /><span className="truncate text-primary/80">{overrides[field.key]}</span></>
                        : <span className="truncate">{mapping[field.key]}</span>
                      }
                    </div>
                  ))}
                </div>
                {metaColMaps.filter(m => m.csvColumn).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5 text-primary/50" /> Metafield mappings
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {metaColMaps.filter(m => m.csvColumn).map(mf => (
                        <div key={`${mf.namespace}.${mf.key}`} className="flex items-center gap-2 text-xs rounded-lg px-3 py-2 bg-muted/50 text-muted-foreground">
                          <Tag className="w-3 h-3 text-primary/50 flex-shrink-0" />
                          <span className="text-primary/80 font-medium truncate">{mf.name}</span>
                          <ArrowLeft className="w-3 h-3 rotate-180 flex-shrink-0" />
                          <span className="truncate">{mf.csvColumn}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Product status */}
              {importMode !== 'patch' && <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">Import products as…</p>
                <div className="flex gap-3">
                  {[
                    { value: 'active', label: 'Active', desc: 'Visible and available for sale immediately' },
                    { value: 'draft', label: 'Draft', desc: 'Saved but hidden from your storefront' },
                    { value: 'archived', label: 'Unlisted', desc: 'Archived and removed from all sales channels' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setProductStatus(opt.value as any)}
                      className={cn(
                        "flex-1 p-3 rounded-xl border text-left transition-all",
                        productStatus === opt.value
                          ? "border-primary bg-primary/10"
                          : "border-border bg-muted/50 hover:border-border"
                      )}
                    >
                      <p className={cn("text-sm font-medium", productStatus === opt.value ? "text-primary" : "text-foreground")}>{opt.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Track quantity */}
                <button
                  onClick={() => setTrackQty(v => !v)}
                  disabled={isImporting}
                  className={cn(
                    "w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all",
                    trackQty
                      ? "border-primary bg-primary/10"
                      : "border-border bg-muted/50 hover:border-border"
                  )}
                >
                  <div>
                    <p className={cn("text-sm font-medium", trackQty ? "text-primary" : "text-foreground")}>
                      Track quantity
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Shopify tracks inventory levels and adjusts stock on each sale
                    </p>
                  </div>
                  <div className={cn(
                    "relative flex-shrink-0 w-10 h-5.5 rounded-full border transition-all ml-4",
                    trackQty ? "bg-primary/30 border-primary/60" : "bg-muted border-border"
                  )}>
                    <span className={cn(
                      "absolute top-0.5 h-4 w-4 rounded-full transition-all",
                      trackQty ? "left-5 bg-primary" : "left-0.5 bg-slate-400"
                    )} />
                  </div>
                </button>

                {/* Continue selling when out of stock */}
                <button
                  onClick={() => setContinueSellingOos(v => !v)}
                  disabled={isImporting}
                  className={cn(
                    "w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all",
                    continueSellingOos
                      ? "border-primary bg-primary/10"
                      : "border-border bg-muted/50 hover:border-border"
                  )}
                >
                  <div>
                    <p className={cn("text-sm font-medium", continueSellingOos ? "text-primary" : "text-foreground")}>
                      Continue selling when out of stock
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Allow customers to purchase even when inventory reaches zero
                    </p>
                  </div>
                  {/* Toggle pill */}
                  <div className={cn(
                    "relative flex-shrink-0 w-10 h-5.5 rounded-full border transition-all ml-4",
                    continueSellingOos ? "bg-primary/30 border-primary/60" : "bg-muted border-border"
                  )}>
                    <span className={cn(
                      "absolute top-0.5 h-4 w-4 rounded-full transition-all",
                      continueSellingOos ? "left-5 bg-primary" : "left-0.5 bg-slate-400"
                    )} />
                  </div>
                </button>
              </div>}

              {/* Product Template */}
              {importMode !== 'patch' && productTemplates.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Product Template</p>
                  <p className="text-xs text-muted-foreground mb-3">Controls which theme template is used to display these products on the storefront.</p>
                  <div className="flex flex-wrap gap-2">
                    {productTemplates.map(t => (
                      <button
                        key={t.value}
                        onClick={() => setTemplateSuffix(t.value)}
                        disabled={isImporting}
                        className={cn(
                          "flex-1 min-w-[7rem] p-3 rounded-xl border text-left transition-all",
                          templateSuffix === t.value
                            ? "border-primary bg-primary/10"
                            : "border-border bg-muted/50 hover:border-border"
                        )}
                      >
                        <p className={cn("text-sm font-medium", templateSuffix === t.value ? "text-primary" : "text-foreground")}>
                          {t.label}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Update mode banner */}
              {importMode === 'update' && (
                <div className="flex items-center gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                  <Pencil className="w-4 h-4 flex-shrink-0" />
                  <span><strong>Update mode</strong> — existing products will be overwritten with data from this CSV. New products from the CSV will also be created.</span>
                </div>
              )}

              {/* Duplicate handling */}
              {importMode === 'import' && (
                <div>
                  <p className="text-sm font-medium text-foreground mb-3">If a product already exists in Shopify…</p>
                  <div className="flex gap-3">
                    {[
                      { value: 'skip', label: 'Skip it', desc: 'Leave existing products unchanged' },
                      { value: 'overwrite', label: 'Overwrite', desc: 'Update with data from this CSV' },
                      { value: 'create', label: 'Always create', desc: 'Create duplicates regardless' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setDuplicateHandling(opt.value as any)}
                        className={cn(
                          "flex-1 p-3 rounded-xl border text-left transition-all",
                          duplicateHandling === opt.value
                            ? "border-primary bg-primary/10"
                            : "border-border bg-muted/50 hover:border-border"
                        )}
                      >
                        <p className={cn("text-sm font-medium", duplicateHandling === opt.value ? "text-primary" : "text-foreground")}>{opt.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Result */}
              {result && (
                <div className={cn(
                  "rounded-xl p-4 border space-y-3",
                  result.errorCount === 0 ? "bg-emerald-500/10 border-emerald-500/20" : "bg-amber-500/10 border-amber-500/20"
                )}>
                  <div className="flex items-center gap-2">
                    {result.errorCount === 0
                      ? <Check className="w-5 h-5 text-emerald-400" />
                      : <AlertCircle className="w-5 h-5 text-amber-400" />}
                    <p className="font-medium text-foreground">{importMode === 'patch' ? 'Patch complete' : 'Import complete'}</p>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <span className="text-emerald-400 font-medium">{result.successCount} {importMode === 'patch' ? 'patched' : 'imported'}</span>
                    {result.skipCount > 0 && <span className="text-muted-foreground">{result.skipCount} skipped</span>}
                    {result.errorCount > 0 && <span className="text-red-400">{result.errorCount} failed</span>}
                  </div>
                  {result.successCount > 0 && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      <span>Products and images may take a few minutes to appear in Shopify — Shopify processes them asynchronously after import. They will all trickle in shortly.</span>
                    </div>
                  )}
                  {result.errors.length > 0 && (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto custom-scrollbar border-t border-red-500/20 pt-3 mt-1">
                      <p className="text-xs font-medium text-red-400 mb-2">Failed products</p>
                      {result.errors.map((e, i) => (
                        <div key={i} className="bg-red-500/5 border border-red-500/15 rounded-lg px-3 py-2">
                          <p className="text-xs font-medium text-foreground">{e.title}</p>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">{cleanShopifyError(e.error)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Live progress bar */}
              {isImporting && importProgress && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{importMode === 'patch' ? 'Patching products…' : 'Importing products…'}</span>
                    <span className="text-foreground font-medium tabular-nums">
                      {importProgress.current.toLocaleString()} / {importProgress.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-300"
                      style={{ width: importProgress.total > 0 ? `${Math.round((importProgress.current / importProgress.total) * 100)}%` : '0%' }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-right">
                    {importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0}% complete
                  </p>
                </div>
              )}

              <div className="flex gap-3 justify-between">
                <Button variant="outline" onClick={() => setStep(2)} disabled={isImporting || !!result}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <div className="flex gap-3">
                  {result && (
                    <Button variant="secondary" onClick={reset}>
                      {importMode === 'update' ? 'Start New Update' : 'Start New Import'}
                    </Button>
                  )}
                  {!result && (
                    <Button onClick={runImport} isLoading={isImporting}>
                      <Package className="w-4 h-4 mr-2" />
                      {isImporting
                        ? (importMode === 'update' ? 'Updating…' : 'Importing…')
                        : importMode === 'update'
                          ? `Update ${productCount != null ? productCount.toLocaleString() : totalRows.toLocaleString()} Products`
                          : `Import ${productCount != null ? productCount.toLocaleString() : totalRows.toLocaleString()} Products`}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import History */}
        {jobs.length > 0 && (
          <div className="glass-panel rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-foreground">Import History</h2>
            <div className="space-y-2">
              {jobs.map((job: any) => {
                const isExpanded = expandedJobId === job.id;
                const jobErrors: { title: string; error: string }[] = Array.isArray(job.errors) ? job.errors : [];
                return (
                  <div key={job.id} className={cn("rounded-xl border transition-all overflow-hidden", job.status === 'running' ? "bg-primary/5 border-primary/20" : "bg-muted/50 border-border/50")}>
                    <div className="p-4 flex items-center gap-4">
                      <FileText className={cn("w-5 h-5 flex-shrink-0", job.status === 'running' ? "text-primary" : "text-muted-foreground")} />
                      <div className="flex-1 overflow-hidden">
                        <p className="text-sm font-medium text-foreground truncate">{job.filename}</p>
                        <p className="text-xs text-muted-foreground">{job.storeName} · {format(new Date(job.createdAt), 'dd MMM yyyy HH:mm')}</p>
                      </div>
                      {job.status !== 'running' && (
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
                          <span className="text-emerald-400 font-medium">{job.successCount} ok</span>
                          {job.skipCount > 0 && <span>{job.skipCount} skipped</span>}
                          {job.errorCount > 0 && (
                            <button
                              onClick={() => setExpandedJobId(isExpanded ? null : job.id)}
                              className="flex items-center gap-1 text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                            >
                              {job.errorCount} failed
                              <ChevronDown className={cn("w-3 h-3 transition-transform", isExpanded && "rotate-180")} />
                            </button>
                          )}
                        </div>
                      )}
                      {job.status === 'running' && (
                        <>
                          <span className="text-xs text-primary font-medium tabular-nums flex-shrink-0">
                            {(job.processedCount ?? 0).toLocaleString()} / {job.totalRows.toLocaleString()}
                          </span>
                          {!isImporting && (
                            <button
                              onClick={() => dismissJob(job.id)}
                              disabled={dismissingJobId === job.id}
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg px-2.5 py-1 transition-all flex-shrink-0 disabled:opacity-50 disabled:pointer-events-none"
                              title="Mark this stuck job as done"
                            >
                              {dismissingJobId === job.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : 'Dismiss'
                              }
                            </button>
                          )}
                        </>
                      )}
                      {statusBadge(job.status)}
                      {(job.status === 'done' || job.status === 'failed') && (
                        <button
                          onClick={() => startUpdate(job)}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary border border-border hover:border-primary/40 rounded-lg px-2.5 py-1.5 transition-all flex-shrink-0"
                          title="Upload a new CSV to update these products"
                        >
                          <Pencil className="w-3 h-3" /> Update
                        </button>
                      )}
                      {job.csvText && (job.status === 'done' || job.status === 'failed') && (
                        <a
                          href={`${BASE}/api/imports/${job.id}/csv`}
                          download={job.filename ?? 'import.csv'}
                          onClick={e => {
                            // Attach auth header by fetching manually then triggering blob download
                            e.preventDefault();
                            fetch(`${BASE}/api/imports/${job.id}/csv`, { headers: authOpts.headers })
                              .then(r => r.blob())
                              .then(blob => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = job.filename ?? 'import.csv';
                                a.click();
                                URL.revokeObjectURL(url);
                              })
                              .catch(() => toast({ title: 'Download failed', variant: 'destructive' }));
                          }}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-sky-400 border border-border hover:border-sky-500/40 rounded-lg px-2.5 py-1.5 transition-all flex-shrink-0"
                          title="Download the original CSV for this import"
                        >
                          <Download className="w-3 h-3" /> CSV
                        </a>
                      )}
                      {job.errorCount > 0 && job.csvText && (job.status === 'done' || job.status === 'failed') && (
                        <button
                          onClick={() => retryImport(job.id)}
                          disabled={retryingJobId !== null}
                          className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 border border-amber-500/20 hover:border-amber-400/40 rounded-lg px-2.5 py-1.5 transition-all flex-shrink-0 disabled:opacity-50 disabled:pointer-events-none"
                          title="Re-run only the products that failed"
                        >
                          {retryingJobId === job.id
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Retrying…</>
                            : <><RefreshCw className="w-3 h-3" /> Retry {job.errorCount} failed</>
                          }
                        </button>
                      )}
                    </div>

                    {/* Running progress bar */}
                    {job.status === 'running' && job.totalRows > 0 && (
                      <div className="px-4 pb-4 space-y-1">
                        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-500"
                            style={{ width: `${Math.round(((job.processedCount ?? 0) / job.totalRows) * 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground text-right">
                          {Math.round(((job.processedCount ?? 0) / job.totalRows) * 100)}% complete
                        </p>
                      </div>
                    )}

                    {/* Retry progress bar */}
                    {retryingJobId === job.id && retryProgress && (
                      <div className="px-4 pb-4 pt-1 space-y-1.5 border-t border-amber-500/20">
                        <div className="flex justify-between text-xs">
                          <span className="text-amber-400 font-medium">Retrying failed products…</span>
                          <span className="text-foreground tabular-nums">{retryProgress.current} / {retryProgress.total}</span>
                        </div>
                        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-amber-400 transition-all duration-300"
                            style={{ width: retryProgress.total > 0 ? `${Math.round((retryProgress.current / retryProgress.total) * 100)}%` : '0%' }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Retry result */}
                    {retryResultMap[job.id] && retryingJobId !== job.id && (() => {
                      const r = retryResultMap[job.id]!;
                      return (
                        <div className="px-4 pb-4 pt-3 border-t border-border space-y-2">
                          <div className="flex items-center gap-2 text-xs">
                            {r.errorCount === 0
                              ? <Check className="w-3.5 h-3.5 text-emerald-400" />
                              : <AlertCircle className="w-3.5 h-3.5 text-amber-400" />}
                            <span className="font-medium text-foreground">Retry complete —</span>
                            <span className="text-emerald-400">{r.successCount} imported</span>
                            {r.skipCount > 0 && <span className="text-muted-foreground">{r.skipCount} already existed</span>}
                            {r.errorCount > 0 && <span className="text-red-400">{r.errorCount} still failing</span>}
                          </div>
                          {r.errors.length > 0 && (
                            <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
                              {r.errors.map((e, i) => (
                                <div key={i} className="bg-red-500/5 border border-red-500/15 rounded-lg px-3 py-1.5">
                                  <p className="text-xs font-medium text-foreground">{e.title}</p>
                                  <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">{cleanShopifyError(e.error)}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Expandable error details */}
                    {isExpanded && jobErrors.length > 0 && (
                      <div className="border-t border-red-500/20 bg-red-500/5 px-4 py-3 space-y-2 max-h-72 overflow-y-auto custom-scrollbar">
                        <p className="text-xs font-medium text-red-400">Failed products ({jobErrors.length})</p>
                        {jobErrors.map((e, i) => (
                          <div key={i} className="bg-muted/50 border border-red-500/15 rounded-lg px-3 py-2">
                            <p className="text-xs font-medium text-foreground">{e.title}</p>
                            <p className="text-xs text-red-700 dark:text-red-300 mt-0.5 break-words">{cleanShopifyError(e.error)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
